"use client";

import { normalizeTicketHours, type TicketHours } from "@/utils/ticket-hours";

import { useEffect, useState } from "react";
import { DEFAULT_CHECKOUT_PAYMENT_FEES, normalizeCheckoutPaymentFees, type CheckoutPaymentFees } from "@/utils/checkout-payment-fees";

interface WinterMode {
    enabled: boolean;
    description: string;
}

interface TemporaryOverride {
    enabled: boolean;
    expiresAt?: string;
}

interface KurtaxePageRedirect {
    enabled: boolean;
}

interface TicketSettings {
    ticketHours: TicketHours | null;
    regularTicketHours: TicketHours | null;
    winterTicketHours: TicketHours | null;
    winterMode: WinterMode | null;
    temporaryOverride: TemporaryOverride | null;
    checkoutPaymentFees: CheckoutPaymentFees;
    kurtaxePageRedirect: KurtaxePageRedirect;
    isTemporaryOverrideActive: boolean;
    isWinterModeActive: boolean;
    userRole: string | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
    refetchSettings: () => void;
}

interface UseTicketSettingsOptions {
    useRawDbValues?: boolean;
}

export function useTicketSettings(_options?: UseTicketSettingsOptions): TicketSettings {
    const [ticketHours, setTicketHours] = useState<TicketHours | null>(null);
    const [regularTicketHours, setRegularTicketHours] = useState<TicketHours | null>(null);
    const [winterTicketHours, setWinterTicketHours] = useState<TicketHours | null>(null);
    const [winterMode, setWinterMode] = useState<WinterMode | null>(null);
    const [temporaryOverride, setTemporaryOverride] = useState<TemporaryOverride | null>(null);
    const [checkoutPaymentFees, setCheckoutPaymentFees] = useState<CheckoutPaymentFees>(DEFAULT_CHECKOUT_PAYMENT_FEES);
    const [kurtaxePageRedirect, setKurtaxePageRedirect] = useState<KurtaxePageRedirect>({ enabled: false });
    const [userRole, setUserRole] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchSettings = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [settingsRes, overrideRes, adminRes] = await Promise.all([
                fetch('/api/ticket-settings'),
                fetch('/api/temporary-override'),
                _options?.useRawDbValues ? fetch('/api/admin/settings') : Promise.resolve(null),
            ]);

            if (settingsRes.ok) {
                const data = await settingsRes.json();
                const settings = data.settings || {};
                const baseHours = normalizeTicketHours(settings.ticket_hours, "regular");
                const winterHours = normalizeTicketHours(settings.ticket_hours_winter, "winter");
                const winterModeSetting = settings.winter_mode || null;
                const normalizedCheckoutFees = normalizeCheckoutPaymentFees(settings.checkout_payment_fees);

                setRegularTicketHours(baseHours);
                setWinterTicketHours(winterHours);
                setWinterMode(winterModeSetting);
                setTicketHours(winterModeSetting?.enabled ? winterHours : baseHours);
                setCheckoutPaymentFees(normalizedCheckoutFees);
            }

            if (overrideRes.ok) {
                const data = await overrideRes.json();
                setTemporaryOverride(data.override || null);
            }

            if (adminRes?.ok) {
                const data = await adminRes.json();
                setUserRole(data.userRole || null);
                if (data.settings?.kurtaxe_page_redirect) {
                    setKurtaxePageRedirect(data.settings.kurtaxe_page_redirect);
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch settings'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const isTemporaryOverrideActive = (() => {
        if (!temporaryOverride?.enabled || !temporaryOverride.expiresAt) return false;
        return new Date() < new Date(temporaryOverride.expiresAt);
    })();

    const isWinterModeActive = winterMode?.enabled ?? false;

    return {
        ticketHours,
        regularTicketHours,
        winterTicketHours,
        winterMode,
        temporaryOverride,
        checkoutPaymentFees,
        kurtaxePageRedirect,
        isTemporaryOverrideActive,
        isWinterModeActive,
        userRole,
        isLoading,
        error,
        refetch: fetchSettings,
        refetchSettings: fetchSettings,
    };
}
